import React, { FC, useEffect, useMemo } from 'react';
import { IHandledDocument, ISearchProfile, ISearchSettings } from '../../types';
import Group from '../Group';
import { useTermOccurrence } from '../../hooks';
import { useTranslation } from 'react-i18next';
import styles from '../index.module.scss';

export interface ISearchGroupProps {
  group: ISearchProfile;
  handledDocuments: IHandledDocument[];
  settings: ISearchSettings;
  selectedValue: string[];
  onUpdateGroup: (groupName: string, updatedValue: string[]) => void;
  onRemove?: () => void;
}

export const SearchGroup: FC<ISearchGroupProps> = props => {
  const { group, settings, handledDocuments, selectedValue, onUpdateGroup, onRemove } = props;
  const { t } = useTranslation();

  const { map: termOccurrenceNumbers } = useTermOccurrence(group.id, handledDocuments, settings);
  const items = useMemo(() => {
    return group.terms
      .filter(term => (termOccurrenceNumbers.get(term) || 0) > 0)
      .map(term => {
        return {
          loading: false,
          occurrences: termOccurrenceNumbers.get(term) || 0,
          value: term,
          label: term
        };
      });
  }, [group.terms, termOccurrenceNumbers]);
  const isSelectAll = selectedValue.length > 0 && selectedValue.length === items.length;
  const groupFormPath = useMemo(() => ['groups', group.id], [group.id]);

  useEffect(() => {
    // in case there is no hits on selected keywords when filtering documents
    const updatedValue = selectedValue.filter(value => !!termOccurrenceNumbers.get(value));
    if (updatedValue.length !== selectedValue.length) {
      onUpdateGroup(group.id, updatedValue);
    }
  }, [group.id, handledDocuments, onUpdateGroup, selectedValue, termOccurrenceNumbers]);

  return (
    <>
      {!!items.length && (
        <Group
          isSelectAll={isSelectAll}
          onSelectGroup={onUpdateGroup}
          emptyMessage={t('KeywordSearch.noTermsAvailable')}
          groupFormPath={groupFormPath}
          title={group.name}
          color={group.colorValue}
          items={items}
          hideCheckbox={true}
          onRemove={onRemove}
        />
      )}
      {!items.length && group.isMpBasedGroup && (
        <span className={styles.warn}>{t('KeywordSearch.mpBasedGroupWarn')} </span>
      )}
    </>
  );
};

export default SearchGroup;
